'use client';

import { Divider, Input } from '@common/components';
import { useState } from 'react';

import {
  ANNUAL_DONATION_LIMIT,
  calculateGiftBenefit,
  calculateTaxDeduction,
  clampToDonationLimit,
} from '../utils';

const BenefitCalculator = () => {
  const [inputValue, setInputValue] = useState('100000');

  const amount = parseInt(inputValue, 10) || 0;
  const taxDeduction = calculateTaxDeduction(amount);
  const giftBenefit = calculateGiftBenefit(amount);
  const totalBenefit = taxDeduction.total + giftBenefit;
  const isAtDonationLimit = amount >= ANNUAL_DONATION_LIMIT;

  return (
    <section className="flex w-full flex-col items-center gap-32 px-64 pb-120 mobile:px-32 mobile:pb-60">
      {/* 헤더 및 입력 영역 */}
      <div className="flex w-full flex-col items-center gap-40 mobile:gap-20">
        <p className="font-500 text-Gray-600 typo-h3 mobile:typo-body3">
          나의 혜택을 직접 계산해보세요!
        </p>

        <div className="flex w-full flex-col items-center gap-8">
          {/* 입력 영역 */}
          <div className="flex items-center gap-16 mobile:gap-8">
            <Input
              type="local-string-number"
              inputMode="numeric"
              value={inputValue}
              onChange={(event) =>
                setInputValue(clampToDonationLimit(event.target.value))
              }
              rightContent="원"
              className="h-56 w-200 mobile:h-48"
            />
            <span className="font-700 text-Gray-black typo-h2 mobile:typo-h5">
              기부하면
            </span>
          </div>

          {/* 총 혜택 요약 */}
          <div className="flex items-center gap-8 font-700 typo-h2 mobile:gap-4 mobile:typo-h5">
            <span className="text-O-500">
              {totalBenefit.toLocaleString('ko-KR')}원
            </span>
            <span className="text-Gray-black">혜택 받아요</span>
          </div>

          {isAtDonationLimit ? (
            <p className="font-500 text-Gray-550 typo-body4 mobile:typo-caption1">
              연간 기부 한도는 최대 2,000만 원이에요.
            </p>
          ) : null}
        </div>
      </div>

      {/* 상세 내역 카드 */}
      <div className="flex w-full max-w-[622px] flex-col gap-48 rounded-large bg-Gray-25 p-48 mobile:gap-24 mobile:p-24">
        {/* 세액공제 */}
        <div className="flex flex-col gap-16 typo-h4 mobile:gap-8 mobile:typo-body4">
          <p className="font-800 text-Gray-900 mobile:font-700">세액공제</p>
          <div className="flex flex-col gap-8 mobile:gap-4">
            {taxDeduction.details.map((detail, index) => (
              <div
                key={index}
                className="flex items-center justify-between font-500 text-Gray-550"
              >
                <span>{detail.label}</span>
                <span>+ {detail.value.toLocaleString('ko-KR')}원</span>
              </div>
            ))}
          </div>
        </div>

        {/* 답례품 */}
        <div className="flex flex-col gap-16 typo-h4 mobile:gap-8 mobile:typo-body4">
          <p className="font-800 text-Gray-900 mobile:font-700">답례품</p>
          <div className="flex items-center justify-between font-500 text-Gray-550">
            <span>지역별 프리미엄 답례품</span>
            <span>+{giftBenefit.toLocaleString('ko-KR')}원</span>
          </div>
          <div className="w-fit rounded-medium bg-Gray-200 px-20 py-8 text-center mobile:rounded-xsmall mobile:px-10 mobile:py-4">
            <p className="font-400 text-Gray-550 typo-h5 mobile:typo-caption1">
              기부금 10만 원당 3만 원 상당 프리미엄 답례품 제공
            </p>
          </div>
        </div>

        <Divider />

        {/* 총 혜택 금액 */}
        <div className="flex items-center justify-between">
          <p className="font-800 text-Gray-900 typo-h4 mobile:font-700 mobile:typo-body4">
            총 혜택 금액
          </p>
          <p className="font-700 text-O-400 typo-h2 mobile:typo-h5">
            {totalBenefit.toLocaleString('ko-KR')}원
          </p>
        </div>
      </div>
    </section>
  );
};

export default BenefitCalculator;
